import { Drawer } from "antd";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { environment } from "../../../consts";
import RequestAccessModal from "../Modals/RequestAccessModal";
import "./HeroSection.scss";

interface HeaderProps {
  isScrolled: boolean;
  windowWidth: number;
}

const Header = ({ isScrolled, windowWidth }: HeaderProps) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const logoVariants = {
    initial: {
      scale: 1,
    },
    scrolled: {
      scale: 0.92,
      transition: {
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  const contactInfoVariants = {
    initial: {
      opacity: 1,
    },
    scrolled: {
      opacity: 0.92,
      transition: {
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  const buttonVariants = {
    initial: {
      scale: 1,
    },
    scrolled: {
      scale: 0.96,
      transition: {
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
    hover: {
      y: -2,
      boxShadow: "0 4px 12px rgba(255, 255, 255, 0.2)",
      borderRadius: "40px",
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  return (
    <>
      <motion.div
        className={`hero-header`}
        animate={
          isScrolled
            ? {
                y: 0,
                opacity: 1,
                backgroundColor: "rgba(17, 30, 45, 0.95)",
                backdropFilter: "blur(15px) saturate(180%)",
                boxShadow:
                  "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
              }
            : {
                y: 0,
                opacity: 1,
                backgroundColor: "rgba(17, 30, 45, 0)",
                backdropFilter: "blur(0px) saturate(100%)",
                boxShadow: "0 0 0 rgba(0, 0, 0, 0)",
              }
        }
        transition={{
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
          backgroundColor: { duration: 0.2 },
          backdropFilter: { duration: 0.2 },
          boxShadow: { duration: 0.2 },
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 3,
        }}
      >
        <motion.div
          className="hero-header-content"
          animate={
            isScrolled
              ? {
                  paddingTop: windowWidth <= 991 ? 15 : 20,
                  paddingBottom: windowWidth <= 991 ? 15 : 20,
                  paddingLeft: windowWidth <= 991 ? 20 : 42,
                  paddingRight: windowWidth <= 991 ? 20 : 42,
                  gap: windowWidth <= 991 ? 15 : 18,
                }
              : {
                  paddingTop: 40,
                  paddingBottom: 40,
                  paddingLeft: 42,
                  paddingRight: 42,
                  gap: 20,
                }
          }
          transition={{
            duration: 0.25,
            ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
          }}
        >
          <motion.div
            className="hero-header-left"
            variants={logoVariants}
            initial="initial"
            animate={isScrolled ? "scrolled" : "initial"}
          >
            <img
              src="/LandingPageImages/Logo.svg"
              alt="EduBlock Pro"
              className="header-logo"
            />
          </motion.div>

          <div className="hero-header-center">
            <motion.div
              className="contact-info"
              variants={contactInfoVariants}
              initial="initial"
              animate={isScrolled ? "scrolled" : "initial"}
            >
              <p className="address-line">Mohali, Punjab</p>
              <p className="address-line">C 208, Phase 8B, Industrial Area</p>
              <p className="address-line">
                Sector 74, Sahibzada Ajit Singh Nagar
              </p>
            </motion.div>
          </div>
          <div className="hero-header-center">
            <motion.div
              className="contact-info"
              variants={contactInfoVariants}
              initial="initial"
              animate={isScrolled ? "scrolled" : "initial"}
            >
              <p className="contact-website">
                <a href="mailto:contact@edublockpro.com">
                  contact@edublockpro.com
                </a>
              </p>
              <p className="contact-phone">
                <a href="tel:8283977734"> +91-8283977734</a>
              </p>
            </motion.div>
          </div>

          <div className="hero-header-right">
            <motion.div
              variants={buttonVariants}
              initial="initial"
              animate={isScrolled ? "scrolled" : "initial"}
              whileHover="hover"
            >
              <button
                onClick={() => setModalOpen(true)}
                className="header-admin-button"
              >
                Exam Authority Registration
                <img
                  src="/LandingPageImages/rightArrow.svg"
                  alt="arrow"
                  className="button-arrow"
                />
              </button>
            </motion.div>
            <div className="header-admin-button-mobile">
              <button
                onClick={() => setModalOpen(true)}
              >
                <img src="/LandingPageImages/loginBtn.svg" alt="login button" />
              </button>
              <button onClick={() => setDrawerOpen(true)}>
                <img src="/LandingPageImages/hamburger.svg" alt="hamburger" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Spacer to maintain header height when fixed */}
      <motion.div
        className="hero-header-spacer"
        animate={
          isScrolled
            ? {
                height: windowWidth <= 991 ? 55 : 80,
              }
            : {
                height: windowWidth <= 991 ? 80 : 120,
              }
        }
        transition={{
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
        }}
      />

      <Drawer
        title={
          <img
            src="/LandingPageImages/Logo.svg"
            alt="EduBlock Pro"
            className="header-logo"
          />
        }
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={320}
      >
        <div>
          <div className="contact-info">
            <p className="contact-location">Mohali, Punjab</p>
            <p className="contact-address">C 208, Phase 8B, Industrial Area</p>
            <p className="contact-address">
              Sector 74, Sahibzada Ajit Singh Nagar
            </p>
          </div>
          <div className="contact-info">
            <p className="contact-website">contact@edublockpro.com</p>
            <p className="contact-phone"> +91-8283977734</p>
          </div>
        </div>
      </Drawer>

      <RequestAccessModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default Header;
